import React, { useId } from 'react';
interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  labelAside?: React.ReactNode;
  error?: string;
  helperText?: React.ReactNode;
  required?: boolean;
}
/**
 * Standardized Maybank textarea — matches the Input component's contrast,
 * focus ring, ARIA wiring, and required marker.
 */
export function Textarea({
  label,
  labelAside,
  error,
  helperText,
  required,
  className = '',
  id,
  rows = 4,
  ...props
}: TextareaProps) {
  const reactId = useId();
  const fieldId = id || props.name || `ta-${reactId}`;
  const helperId = helperText ? `${fieldId}-help` : undefined;
  const errorId = error ? `${fieldId}-err` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;
  return <div className="w-full" data-id="element-1025">
      {label && <div className="flex items-baseline justify-between mb-1.5 gap-3" data-id="element-1026">
          <label htmlFor={fieldId} className="block text-sm font-medium text-gray-900" data-id="element-1027">
            {label}
            {required && <span className="text-red-600 ml-0.5" aria-hidden="true" data-id="element-1028">
                *
              </span>}
            {required && <span className="sr-only" data-id="element-1029"> (required)</span>}
          </label>
          {labelAside && <span className="text-xs text-gray-600 tabular-nums" data-id="element-1030">
              {labelAside}
            </span>}
        </div>}
      <textarea id={fieldId} rows={rows} required={required} aria-invalid={error ? true : undefined} aria-describedby={describedBy} className={`
          block w-full rounded-lg border-2 shadow-sm bg-white
          text-gray-900 placeholder-gray-500
          focus:outline-none focus:border-[#B8860B] focus:ring-2 focus:ring-[#B8860B] focus:ring-offset-1
          disabled:bg-gray-100 disabled:text-gray-600 disabled:border-gray-300
          sm:text-sm py-3 px-3 resize-y
          ${error ? 'border-red-600 focus:border-red-700 focus:ring-red-600' : 'border-gray-500'}
          ${className}
        `} {...props} data-id="element-1031" />
      {helperText && !error && <p id={helperId} className="mt-1 text-xs text-gray-600" data-id="element-1032">
          {helperText}
        </p>}
      {error && <p id={errorId} className="mt-1 text-sm text-red-700 font-medium" role="alert" data-id="element-1033">
          {error}
        </p>}
    </div>;
}